import { getClassLevel } from './classIdentity.mjs';

// 2024 Paladin rules (SRD 5.2). Feature levels run to 20; automation above level 5 lands in batches. Later batches add Weapon Mastery, Fighting Style
// (Blessed Warrior), Lay on Hands, Paladin's Smite, Channel Divinity and Faithful Steed on top of these.
export const DIVINE_SMITE_SPELL_ID = 'divine-smite';
export const FIND_STEED_SPELL_ID = 'find-steed';
/** Feature grants for the always-prepared spells (one free cast each per Long Rest). */
export const PALADINS_SMITE_GRANT_ID = 'paladin-smite-divine-smite';
export const FAITHFUL_STEED_GRANT_ID = 'paladin-faithful-steed-find-steed';

export const PALADIN_FEATURE_LEVELS = Object.freeze({
  layOnHands: 1,
  spellcasting: 1,
  weaponMastery: 1,
  fightingStyle: 2,
  paladinsSmite: 2,
  channelDivinity: 3,
  subclass: 3,
  extraAttack: 5,
  faithfulSteed: 5,
  auraOfProtection: 6,
  abjureFoes: 9,
  auraOfCourage: 10,
  radiantStrikes: 11,
  restoringTouch: 14,
  auraExpansion: 18,
});

export const getPaladinLevel = character => getClassLevel(character, 'paladin');

export const hasPaladinFeature = (character, feature) => {
  const level = getPaladinLevel(character);
  const required = PALADIN_FEATURE_LEVELS[feature];
  return level > 0 && Number.isInteger(required) && level >= required;
};

/** Lay On Hands: a healing pool of 5 × Paladin level, refilled after a Long Rest. */
export const layOnHandsPool = level => (level < 1 ? 0 : 5 * level);

/** Pool points that remove the Poisoned condition; they don't also restore Hit Points. */
export const LAY_ON_HANDS_CURE_COST = 5;

/** Pool state lives at `classState.paladin.layOnHands` as points spent, so a new Paladin level grows the remainder. */
export const getLayOnHandsProfile = character => {
  const level = getPaladinLevel(character);
  const maximum = layOnHandsPool(level);
  const state = character?.classState?.paladin?.layOnHands || {};
  const spent = Math.min(maximum, Math.max(0, Math.floor(Number(state.spent) || 0)));
  return { level, maximum, spent, remaining: maximum - spent, revision: Math.max(0, Number(state.revision) || 0) };
};

/** Points one Bonus Action spends: the Hit Points restored plus 5 if it also removes Poisoned. */
/** Restoring Touch (Paladin 14): Lay On Hands can also remove these, 5 pool points each (no Hit Points restored). */
export const RESTORING_TOUCH_CONDITIONS = Object.freeze(['blinded', 'charmed', 'deafened', 'frightened', 'paralyzed', 'stunned']);

export const layOnHandsCost = ({ healing = 0, curePoisoned = false, removeConditions = [] } = {}) =>
  Math.max(0, Math.floor(Number(healing) || 0)) + (curePoisoned ? LAY_ON_HANDS_CURE_COST : 0)
  + (Array.isArray(removeConditions) ? removeConditions.length : 0) * LAY_ON_HANDS_CURE_COST;

/** A Long Rest refills the pool; a Short Rest leaves it unchanged (null means no update). */
export const layOnHandsRestState = (character, type) => {
  if (type !== 'long' || getPaladinLevel(character) < PALADIN_FEATURE_LEVELS.layOnHands) return null;
  return { spent: 0, revision: getLayOnHandsProfile(character).revision + 1 };
};

/** Merge a server pool state unless the sheet already holds a newer revision. */
export const applyLayOnHandsUpdate = (character, layOnHands, changes = {}) => {
  if (!layOnHands || Number(character?.classState?.paladin?.layOnHands?.revision || 0) > Number(layOnHands.revision || 0)) return character;
  return { ...character, ...changes, classState: { ...character?.classState, paladin: { ...character?.classState?.paladin, layOnHands } } };
};

/** Channel Divinity uses (Paladin table): 2 from level 3, 3 from level 11. */
export const channelDivinityUses = level => (level < 3 ? 0 : level < 11 ? 2 : 3);

/** Weapon kinds a Paladin masters; the count does not grow with level. */
export const paladinMasteryCount = level => (level < 1 ? 0 : 2);

/** Free casts per Long Rest: Divine Smite from level 2 (Paladin's Smite), Find Steed from 5 (Faithful Steed). */
export const paladinsSmiteFreeCasts = level => (level >= PALADIN_FEATURE_LEVELS.paladinsSmite ? 1 : 0);
export const faithfulSteedFreeCasts = level => (level >= PALADIN_FEATURE_LEVELS.faithfulSteed ? 1 : 0);

/** Divine Smite's on-hit decision and its per-turn record share this feature id. */
export const DIVINE_SMITE_FEATURE_ID = 'paladin:divine-smite';

const creatureTypeOf = entity => {
  const type = entity?.creatureType ?? entity?.type;
  return String(type?.index ?? type?.name ?? type ?? '').trim().toLowerCase();
};
/** Fiends and Undead take an extra 1d8 from Divine Smite (monster records keep `type`, e.g. "undead"). */
export const isFiendOrUndead = entity => /^(fiend|undead)\b/.test(creatureTypeOf(entity));

/**
 * 2024 Divine Smite: 2d8 Radiant, +1d8 for each slot level above 1, +1d8 against a Fiend or Undead.
 * The 2014 cap of 5d8 (6d8 against Fiends and Undead) is not part of the 2024 spell.
 */
export const divineSmiteDice = ({ slotLevel = 1, fiendOrUndead = false } = {}) =>
  2 + Math.max(0, Math.floor(Number(slotLevel) || 1) - 1) + (fiendOrUndead ? 1 : 0);

/** Paladin's Smite (2): Divine Smite is always prepared. Before then a Paladin can still prepare it as a spell. */
export const canCastDivineSmite = character => hasPaladinFeature(character, 'paladinsSmite')
  || (character?.spells || []).some(spell => spell?.spellId === DIVINE_SMITE_SPELL_ID);

/** Casts without a slot left this Long Rest (shared per-rest counter, reset by a Long Rest). */
export const paladinsSmiteFreeRemaining = character => {
  const maximum = paladinsSmiteFreeCasts(getPaladinLevel(character));
  const used = Math.max(0, Math.floor(Number(character?.spellcastingState?.speciesFreeCasts?.[PALADINS_SMITE_GRANT_ID]) || 0));
  return Math.max(0, maximum - used);
};

/** Divine Smite and Radiant Strikes follow a hit with a Melee weapon or an Unarmed Strike. */
export const isSmiteAttack = attack => attack?.kind === 'unarmed' || (attack?.kind === 'weapon' && attack?.isMelee === true);

/** Divine Sense (Channel Divinity): a 10-minute effect on the Paladin that senses these creature types within 60 feet. */
export const DIVINE_SENSE_EFFECT_ID = 'paladin:divine-sense';
export const DIVINE_SENSE_RANGE_FEET = 60;
export const isDivineSenseCreature = entity => /^(celestial|fiend|undead)\b/.test(creatureTypeOf(entity));
/** Its creature type as Divine Sense reports it ("Undead"), or null. */
export const divineSenseCreatureType = entity => {
  const match = creatureTypeOf(entity).match(/^(celestial|fiend|undead)\b/);
  return match ? match[1].charAt(0).toUpperCase() + match[1].slice(1) : null;
};
/** Channel Divinity effects last 10 minutes: 100 rounds, ending at the end of a round. */
export const TEN_MINUTES_ROUNDS = 100;

/** Faithful Steed (5): Find Steed is always prepared, with one cast per Long Rest that needs no slot. */
export const faithfulSteedFreeRemaining = character => {
  const maximum = faithfulSteedFreeCasts(getPaladinLevel(character));
  const used = Math.max(0, Math.floor(Number(character?.spellcastingState?.speciesFreeCasts?.[FAITHFUL_STEED_GRANT_ID]) || 0));
  return Math.max(0, maximum - used);
};

/**
 * 2024 Searing Smite: also a Bonus Action after a Melee weapon or Unarmed Strike hit. 1d6 Fire, +1d6 for each slot
 * level above 1; the target then burns (the same dice of Fire at the start of each of its turns, then a Constitution
 * save ends the spell). Up to 1 minute, no concentration. It needs a slot: Paladin's Smite's free use is Divine Smite only.
 */
export const SEARING_SMITE_SPELL_ID = 'searing-smite';
export const searingSmiteDice = slotLevel => Math.max(1, Math.floor(Number(slotLevel) || 1));
/** A Paladin prepared Searing Smite (a spell they chose; it is not always prepared). */
export const canCastSearingSmite = character => (character?.spells || []).some(spell => spell?.spellId === SEARING_SMITE_SPELL_ID);

/**
 * 2024 Shining Smite (level 2): after a Melee weapon or Unarmed Strike hit, 2d6 Radiant (+1d6 for each slot level
 * above 2). Concentration, up to 1 minute: the target sheds Bright Light in a 5-foot radius, attack rolls against it
 * have Advantage, and it can't benefit from the Invisible condition.
 */
export const SHINING_SMITE_SPELL_ID = 'shining-smite';
export const shiningSmiteDice = slotLevel => 2 + Math.max(0, Math.floor(Number(slotLevel) || 2) - 2);
export const canCastShiningSmite = character => (character?.spells || []).some(spell => spell?.spellId === SHINING_SMITE_SPELL_ID);
